import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as ArrowDownIcon } from '../../../../common/images/arrow-down.svg';
import styles from './index.module.css';

// Constants
const LOCATIONS_DISPLAY_LIMIT = 5;

// Types
export type Location = {
  name: string;
  children: { name: string }[];
};

type LocationsDisplayHook = {
  displayedLocations: Location[];
  hasMoreLocations: boolean;
  expandedLocations: Record<number, boolean>;
  showAllLocations: boolean;
  isContentExpanded: boolean;
  toggleLocation: (index: number) => void;
  toggleShowAll: () => void;
};

// Custom hook for locations display logic
function useLocationsDisplay(locations?: Location[]): LocationsDisplayHook {
  const [expandedLocations, setExpandedLocations] = useState<Record<number, boolean>>({});
  const [showAllLocations, setShowAllLocations] = useState(false);

  const toggleLocation = useCallback((index: number) => {
    setExpandedLocations((prev) => ({ ...prev, [index]: !prev[index] }));
  }, []);

  const displayedLocations = useMemo(() => {
    if (!locations || locations.length === 0) return [];
    return showAllLocations ? locations : locations.slice(0, LOCATIONS_DISPLAY_LIMIT);
  }, [locations, showAllLocations]);

  // Calculate if expanded children cause overflow
  const hasExpandedChildrenOverflow = useMemo(() => {
    if (!displayedLocations.length) return false;
    const totalVisibleItems = displayedLocations.reduce((total, location, index) => {
      const isExpanded = expandedLocations[index] || false;
      const childrenCount = isExpanded && location.children ? location.children.length : 0;
      return total + 1 + childrenCount; // +1 for the location itself
    }, 0);
    return totalVisibleItems > LOCATIONS_DISPLAY_LIMIT;
  }, [displayedLocations, expandedLocations]);

  const hasMoreLocations = useMemo(() => {
    // Check if there are more location items than the limit
    const hasMoreItems = (locations?.length || 0) > LOCATIONS_DISPLAY_LIMIT;

    // Show button if there are more items OR if expanded children cause overflow
    return hasMoreItems || hasExpandedChildrenOverflow;
  }, [locations, hasExpandedChildrenOverflow]);

  // Determine if content is currently "expanded" (either showAllLocations OR expanded children causing overflow)
  const isContentExpanded = useMemo(() => {
    return showAllLocations || hasExpandedChildrenOverflow;
  }, [showAllLocations, hasExpandedChildrenOverflow]);

  const toggleShowAll = useCallback(() => {
    if (isContentExpanded) {
      // Collapsing: reset showAllLocations and collapse all children
      setShowAllLocations(false);
      setExpandedLocations({});
    } else {
      // Expanding: show all locations
      setShowAllLocations(true);
    }
  }, [isContentExpanded]);

  return {
    displayedLocations,
    hasMoreLocations,
    expandedLocations,
    showAllLocations,
    isContentExpanded,
    toggleLocation,
    toggleShowAll,
  };
}

// LocationItem component
type LocationItemProps = {
  location: Location;
  index: number;
  isExpanded: boolean;
  onToggle: (index: number) => void;
};

function LocationItem({ location, index, isExpanded, onToggle }: LocationItemProps) {
  const hasChildren = location.children && location.children.length > 0;

  const handleClick = () => {
    if (hasChildren) {
      onToggle(index);
    }
  };

  return (
    <div className={styles.locationItem}>
      <div
        className={styles.locationHeader}
        onClick={handleClick}
        style={{ cursor: hasChildren ? 'pointer' : 'default' }}
      >
        {hasChildren && (
          <ArrowDownIcon
            className={`${styles.locationChevron} ${
              isExpanded ? styles.locationChevronExpanded : ''
            }`}
          />
        )}
        <span className={styles.locationName}>{location.name}</span>
      </div>
      {hasChildren && isExpanded && (
        <ul className={styles.locationChildren}>
          {location.children.map((child, childIndex) => (
            <li key={childIndex} className={styles.locationChild}>
              {child.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// LocationsList component
export type LocationsListProps = {
  locations?: Location[];
  formTopRef: React.RefObject<HTMLDivElement>;
};

export function LocationsList({ locations, formTopRef }: LocationsListProps) {
  const {
    displayedLocations,
    hasMoreLocations,
    expandedLocations,
    isContentExpanded,
    toggleLocation,
    toggleShowAll,
  } = useLocationsDisplay(locations);

  const previousIsExpandedRef = useRef(isContentExpanded);

  // Scroll to top of form when "Show less" is clicked (when content collapses)
  useEffect(() => {
    if (
      previousIsExpandedRef.current === true &&
      isContentExpanded === false &&
      formTopRef.current
    ) {
      formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    previousIsExpandedRef.current = isContentExpanded;
  }, [isContentExpanded, formTopRef]);

  if (!locations || locations.length === 0) {
    return <span>No locations</span>;
  }

  return (
    <div className={styles.locationsList}>
      {displayedLocations.map((location, index) => (
        <LocationItem
          key={`${location.name}-${index}`}
          location={location}
          index={index}
          isExpanded={expandedLocations[index] || false}
          onToggle={toggleLocation}
        />
      ))}
      {hasMoreLocations && (
        <button type="button" onClick={toggleShowAll} className={styles.viewAllLink}>
          {isContentExpanded ? 'Show less' : 'View All'}
        </button>
      )}
    </div>
  );
}
